import { LocalDateTime } from "@js-joda/core";
import React from "react";
import RequiredDocDropdownActivity from "../../../activities/RequiredDocDropdownActivity";
import {
  VitalAnswerType,
  VitalMethodNames,
  VitalMethodType,
  VitalType,
  VitalUnitNames,
  VitalUnitType,
} from "../../../messages/Vital";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { If } from "../../Generic/If";
import QuestionHeader from "../../QuestionHeader";
import { RequiredDocAnswerableQuestionProps } from "../../RequiredDocAnswerableQuestion";
import RequiredDocBloodPressure from "../../RequiredDocBloodPressure";
import RequiredDocDateTime from "../../RequiredDocDateTime";
import RequiredDocNumber from "../../RequiredDocNumber";
import RequiredDocShortText from "../../RequiredDocShortText";
import classNames from "classnames";

interface Props extends RequiredDocAnswerableQuestionProps {
  questionText: string;
  vitalMeasuredAt: LocalDateTime | null;
  vitalMethod: VitalMethodType | null;
  vitalComments: string | null;
  vitalUnit: VitalUnitType | null;
  readonly: boolean;
  vitalType: VitalType;
  unitTypeOptions: string[];
  methodTypeOptions: string[];
  onVitalAnswerChange: (
    id: number,
    answer: string | string[] | null,
    vitalAnswerType: VitalAnswerType
  ) => void;
  onInsertVitalMethod: (id: number, answer: string | string[] | null) => void;
  onInsertVitalMeasuredAt: (id: number, answer: string | string[] | null) => void;
  onInsertVitalComments: (id: number, answer: string | string[] | null) => void;
  onInsertVitalUnit: (id: number, answer: string | string[] | null) => void;
  handleClickWhileDisabled: () => void;
}

const RequiredDocVitalSign = ({
  questionId,
  questionText,
  currentAnswer,
  vitalMeasuredAt,
  vitalMethod,
  vitalComments,
  vitalUnit,
  readonly,
  vitalType,
  notes,
  isValid,
  isRequired,
  unitTypeOptions,
  methodTypeOptions,
  onVitalAnswerChange,
  onInsertVitalMethod,
  onInsertVitalMeasuredAt,
  onInsertVitalComments,
  onInsertVitalUnit,
  handleClickWhileDisabled,
}: Props) => {
  return (
    <AppCard className={classNames("RequiredDocVitalSign", { __invalid: !isValid })}>
      <QuestionHeader
        text={questionText}
        isRequired={isRequired}
        notes={null}
        isReadonly={readonly}
      />
      <AppCardContent className="RequiredDocVitalSign__vitalsContainer">
        <RequiredDocDropdownActivity
          questionId={questionId}
          questionText={"Method"}
          prefilledAnswer={null}
          currentAnswer={vitalMethod !== null ? VitalMethodNames.get(vitalMethod) : null}
          handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
            onInsertVitalMethod(id, answer)
          }
          showOtherOption={false}
          possibleAnswers={methodTypeOptions}
          isRequired={false}
          isValid={true}
          readonly={readonly}
          notes={null}
          onClickWhileDisabled={() => handleClickWhileDisabled}
        />

        <RequiredDocDateTime
          questionId={questionId}
          questionText={"Measured At"}
          prefilledAnswer={null}
          handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
            onInsertVitalMeasuredAt(id, answer)
          }
          currentAnswer={vitalMeasuredAt === null ? null : vitalMeasuredAt.toString()}
          isRequired={false}
          isValid={true}
          readonly={readonly}
          notes={null}
          onClickWhileDisabled={() => handleClickWhileDisabled}
        />

        <RequiredDocShortText
          questionId={questionId}
          handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
            onInsertVitalComments(id, answer)
          }
          questionText={"Comments"}
          prefilledAnswer={null}
          currentAnswer={vitalComments}
          readonly={readonly}
          notes={null}
          isRequired={false}
          isValid={true}
          onClickWhileDisabled={() => handleClickWhileDisabled}
        />

        <RequiredDocDropdownActivity
          questionId={questionId}
          questionText={"Unit"}
          prefilledAnswer={null}
          currentAnswer={vitalUnit !== null ? VitalUnitNames.get(vitalUnit) : null}
          handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
            onInsertVitalUnit(id, answer)
          }
          showOtherOption={false}
          possibleAnswers={unitTypeOptions}
          isRequired={false}
          isValid={true}
          readonly={readonly}
          notes={null}
          onClickWhileDisabled={() => handleClickWhileDisabled}
        />

        <If condition={vitalType === "BLOOD_PRESSURE"}>
          <RequiredDocBloodPressure
            questionId={questionId}
            firstNumber={
              currentAnswer === undefined ||
              currentAnswer === null ||
              typeof currentAnswer === "string"
                ? null
                : currentAnswer[0]
            }
            secondNumber={
              currentAnswer === undefined ||
              currentAnswer === null ||
              typeof currentAnswer === "string"
                ? null
                : currentAnswer[1]
            }
            questionText={"Blood Pressure"}
            prefilledAnswer={null}
            notes={null}
            isRequired={false}
            isValid={true}
            readonly={readonly}
            handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
              onVitalAnswerChange(id, answer, "BASE_ANSWER")
            }
          />
        </If>

        <If condition={vitalType === "REGULAR_NUMBER"}>
          <RequiredDocNumber
            questionId={questionId}
            questionText={questionText}
            prefilledAnswer={null}
            currentAnswer={currentAnswer}
            isRequired={isRequired}
            isValid={isValid}
            readonly={readonly}
            notes={notes}
            handleOnInsertAnswer={(id: number, answer: string | string[] | null) =>
              onVitalAnswerChange(id, answer, "BASE_ANSWER")
            }
            onClickWhileDisabled={() => handleClickWhileDisabled}
          />
        </If>
      </AppCardContent>
    </AppCard>
  );
};

export default React.memo(RequiredDocVitalSign);
